import { ReactNode } from "react";
import { X } from "@digital-twins/design-system/icons";
import { cn } from "@/lib/utils";

/** The shape every filter pill and token shares. */
export const PILL_CLASS = cn(
  "inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border text-sm font-medium",
  "transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
);

/** An idle pill: a hairline outline that opens its menu. */
export const IDLE_PILL_CLASS = cn(
  PILL_CLASS,
  "group border-border bg-background pl-3 pr-2.5 hover:bg-accent aria-expanded:border-ring",
);

export interface SocialFilterTokenProps {
  /** The dimension's name, shown muted before the value. */
  label?: string;
  value: ReactNode;
  removeLabel: string;
  onRemove: () => void;
  testId: string;
  /** Extra classes on the token, e.g. to lift the width cap inside a scrolling strip. */
  className?: string;
  /** The pill button that reopens the menu; absent for the date and author tokens. */
  children?: ReactNode;
}

/** An active filter: a primary-tinted token with a removable ×. */
export function SocialFilterToken(props: SocialFilterTokenProps) {
  const { label, value, removeLabel, onRemove, testId, className, children } = props;

  const body = (
    <>
      {label ? <span className="font-normal text-muted-foreground">{label}</span> : null}
      <span className="inline-flex min-w-0 max-w-48 items-center gap-1.5">{value}</span>
    </>
  );

  return (
    <span
      className={cn(PILL_CLASS, "max-w-full border-primary/45 bg-primary/10 pr-1", className)}
      data-testid={`${testId}-token`}
    >
      {children ?? (
        <span className="inline-flex min-w-0 items-center gap-1.5 pl-3" data-testid={testId}>
          {body}
        </span>
      )}
      <button
        type="button"
        aria-label={removeLabel}
        className="inline-flex size-6 shrink-0 items-center justify-center rounded-full text-foreground hover:bg-foreground/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={onRemove}
        data-testid={`${testId}-clear`}
      >
        <X className="size-3" aria-hidden="true" />
      </button>
    </span>
  );
}
